import { useCallback } from "react";

import {
	PaymentMethod,
	Reservation_List_Repeater_Type,
	Reservation_List_Reservation_Status,
	Reservation_List_Reservation_Type,
} from "@/lib/gql/__generated__/graphql";

import type * as Shared from "../../../_shared";
import type * as Repository from "../repository";
import type * as Type from "../type";

type QueryData = Repository.Query.QueryData.ReservationRequestList;
type ViewData = Type.ViewData;
type ReservationType = Shared.Model.Type.ViewData.SearchResultTableRowDataOfReservationType;

type Props = {
	queryData: QueryData;
	filteredQueryData: QueryData;
};

export const useViewDataConverter = () => {
	/**
	 * 検索キーの変換
	 */
	const getDetailSearch = useCallback((queryData: QueryData): ViewData["detailSearch"] => {
		return {
			/**
			 * 表示制御
			 */
			canShowDetailSearchSection: queryData.reservationList.length > 0,
		} satisfies ViewData["detailSearch"];
	}, []);
	/**
	 * 予約タイプの変換
	 */
	const getReservationTypeForRows = useCallback(
		(reservationType: Reservation_List_Reservation_Type): ReservationType => {
			if (reservationType === Reservation_List_Reservation_Type.RefundCompleted) {
				return {
					text: "返金対応済",
					labelType: "pink",
				} satisfies ReservationType;
			}

			if (reservationType === Reservation_List_Reservation_Type.DayTrip) {
				return {
					text: "日帰り",
					labelType: "green",
				} satisfies ReservationType;
			}

			if (reservationType === Reservation_List_Reservation_Type.TestReservation) {
				return {
					text: "テスト予約",
					labelType: "green",
				} satisfies ReservationType;
			}

			if (reservationType === Reservation_List_Reservation_Type.HostReservation) {
				return {
					text: "直接予約入力",
					labelType: "pink",
				} satisfies ReservationType;
			}

			return {
				text: "ゲスト予約",
				labelType: "link",
			} satisfies ReservationType;
		},
		[],
	);
	/**
	 * 予約の状態の変換
	 */
	const getReservationStatusTextForRows = useCallback((reservationStatus: Reservation_List_Reservation_Status) => {
		if (reservationStatus === Reservation_List_Reservation_Status.NewRequest) return "新着リクエスト";

		if (reservationStatus === Reservation_List_Reservation_Status.Reserved) return "予約中";

		if (reservationStatus === Reservation_List_Reservation_Status.OnsitePaymentForPaymentError)
			return "決済エラーにより現地徴収";

		if (reservationStatus === Reservation_List_Reservation_Status.CancelledByPaymentError)
			return "決済エラーによるキャンセル";

		if (reservationStatus === Reservation_List_Reservation_Status.Cancelled) return "キャンセル";

		return "-";
	}, []);
	/**
	 * テーブルデータの変換
	 */
	const getRows = useCallback(
		({ reservationList }: QueryData): ViewData["searchResultTableData"]["rows"] =>
			reservationList.map(
				({
					hotelID,
					reservationID,
					reservationType,
					reservationStatus,
					guestName,
					checkIn,
					checkOut,
					reserveDate,
					totalGuestCharge,
					paymentMethod,
					numOfPeople,
					room,
					plan,
					option,
					discount,
					guestTel,
					guestEmail,
					guestPostalCode,
					guestAddress,
					isAcceptMailMagazine,
					repeaterType,
				}) => {
					return {
						// ホテルID
						hotelID,
						// 予約ID
						reservationID,
						// 予約番号
						reservationIDText: reservationID,
						// 予約タイプ
						reservationTypes: reservationType.map((type) => getReservationTypeForRows(type)),
						// 日帰り予約かどうか
						isDayTrip: reservationType.includes(Reservation_List_Reservation_Type.DayTrip),
						// 状態
						reservationStatusText: getReservationStatusTextForRows(reservationStatus),
						// 氏名
						guestNameText: guestName,
						// チェックイン
						checkInText: checkIn,
						// チェックアウト
						checkOutText: checkOut,
						// 予約日
						reserveDateText: reserveDate,
						// 合計料金（税込）
						totalGuestChargeText: totalGuestCharge.toLocaleString(),
						// 決済方法
						paymentMethodText: paymentMethod === PaymentMethod.Credit ? "事前決済" : "現地決済",
						// 人数
						numOfPeopleText: `${numOfPeople}名`,
						// 部屋
						roomsText: room.length > 0 ? room.map(({ name }) => name).join(`\n`) : "-",
						// プラン名
						planNameText: plan ?? "-",
						// オプション
						optionText: option.map((option) => `${option.name}${option.amount}個`).join(`\n`),
						// ディスカウント
						discountText: discount ?? "-",
						// 電話番号
						guestTelText: guestTel,
						// メールアドレス
						guestEmailText: guestEmail,
						// 郵便番号
						guestPostalCodeText: guestPostalCode ? `〒${guestPostalCode}` : "",
						// 住所
						guestAddressText: guestAddress,
						// メルマガ配信希望
						isAcceptMailMagazineText: isAcceptMailMagazine ? "⚪︎" : "×",
						// リピータタイプ
						repeaterType: repeaterType ?? Reservation_List_Repeater_Type.NotRepeater,
					};
				},
			) satisfies ViewData["searchResultTableData"]["rows"],
		[getReservationStatusTextForRows, getReservationTypeForRows],
	);
	/**
	 * 検索結果のテーブルデータへの変換
	 */
	const getSearchResultTableData = useCallback(
		(queryData: QueryData): ViewData["searchResultTableData"] => {
			/**
			 * テーブル行データ
			 */
			const rows = getRows(queryData);

			return {
				rows,
			} satisfies ViewData["searchResultTableData"];
		},
		[getRows],
	);

	const onConvertViewData = useCallback(
		({ queryData, filteredQueryData }: Props): ViewData => {
			const detailSearch = getDetailSearch(queryData);
			const searchResultTableData = getSearchResultTableData(filteredQueryData);

			return {
				detailSearch,
				csvDownloadData: filteredQueryData,
				searchResultTableData,
			} satisfies ViewData;
		},
		[getDetailSearch, getSearchResultTableData],
	);

	return {
		onConvertViewData,
	};
};
